import React from 'react';
import { motion } from 'framer-motion';
import { Star, Check, AlertTriangle, XCircle, ScanLine, Smartphone } from 'lucide-react';
export function HeroSection() {
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-77">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden" data-id="element-78">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-brand-100/40 rounded-full blur-3xl opacity-50" data-id="element-79" />
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-accent-100/40 rounded-full blur-3xl opacity-40" data-id="element-80" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-81">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-82">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left" data-id="element-83">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-brand-100 text-brand-700 text-sm font-medium mb-6" data-id="element-84">
              <span className="flex h-2 w-2 rounded-full bg-brand-500 mr-2 animate-pulse" data-id="element-85"></span>
              Now available on iOS & Android
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-tight mb-6" data-id="element-86">
              Know What You're <br className="hidden lg:block" data-id="element-87" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-500" data-id="element-88">
                Really Eating
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-89">
              Scan any packaged food to instantly detect unhealthy ingredients.
              Make smarter choices with AI-powered ingredient analysis.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10" data-id="element-90">
              <a href="https://apps.apple.com/us/app/intellieats/id6744873855" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-91">
                <button className="w-full sm:w-auto flex items-center justify-center px-6 py-3.5 bg-white text-slate-900 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all hover:scale-105 duration-200 shadow-lg" data-id="element-92">
                  <svg className="w-6 h-6 mr-3 text-slate-900" viewBox="0 0 24 24" fill="currentColor" data-id="element-93">
                    <path d="M3,20.5V3.5C3,2.91,3.34,2.39,3.84,2.15L13.69,12L3.84,21.85C3.34,21.6,3,21.09,3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08,20.75,11.5,20.75,12C20.75,12.5,20.5,12.92,20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" data-id="element-94" />
                  </svg>
                  <div className="text-left" data-id="element-95">
                    <div className="text-[10px] uppercase font-medium opacity-80" data-id="element-96">
                      Download on the
                    </div>
                    <div className="text-sm font-bold leading-none" data-id="element-97">
                      App Store
                    </div>
                  </div>
                </button>
              </a>

              <a href="https://play.google.com/store/apps/details?id=com.Lakh.intelli_eats&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-98">
                <button className="w-full sm:w-auto flex items-center justify-center px-6 py-3.5 bg-white text-slate-900 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all hover:scale-105 duration-200 shadow-lg" data-id="element-99">
                  <svg className="w-6 h-6 mr-3 text-slate-900" viewBox="0 0 24 24" fill="currentColor" data-id="element-100">
                    <path d="M3,20.5V3.5C3,2.91,3.34,2.39,3.84,2.15L13.69,12L3.84,21.85C3.34,21.6,3,21.09,3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08,20.75,11.5,20.75,12C20.75,12.5,20.5,12.92,20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" data-id="element-101" />
                  </svg>
                  <div className="text-left" data-id="element-102">
                    <div className="text-[10px] uppercase font-medium opacity-80" data-id="element-103">
                      Get it on
                    </div>
                    <div className="text-sm font-bold leading-none" data-id="element-104">
                      Google Play
                    </div>
                  </div>
                </button>
              </a>
            </div>

            <div className="flex items-center justify-center lg:justify-start space-x-8 text-sm font-medium text-slate-500" data-id="element-105">
              <div className="flex items-center" data-id="element-106">
                <div className="flex text-accent-500 mr-2" data-id="element-107">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-108" />)}
                </div>
                <span data-id="element-109">5.0 Rating</span>
              </div>
              <div className="w-px h-4 bg-slate-300" data-id="element-110"></div>
              <div data-id="element-111">Free Scanner</div>
              <div className="w-px h-4 bg-slate-300" data-id="element-112"></div>
              <div data-id="element-113">1k+ Scans</div>
            </div>
          </motion.div>

          {/* Phone Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative mx-auto lg:mr-0 max-w-[320px] lg:max-w-[360px]" data-id="element-114">
            {/* Floating Elements */}
            <motion.div animate={{
            y: [0, -15, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -right-8 top-20 z-20 bg-white p-3 rounded-2xl shadow-xl border border-slate-100 hidden sm:block" data-id="element-115">
              <div className="flex items-center gap-3" data-id="element-116">
                <div className="bg-red-100 p-2 rounded-full" data-id="element-117">
                  <XCircle className="w-5 h-5 text-red-600" data-id="element-118" />
                </div>
                <div data-id="element-119">
                  <div className="text-xs text-slate-500 font-medium" data-id="element-120">
                    Detected
                  </div>
                  <div className="text-sm font-bold text-slate-900" data-id="element-121">Red 40</div>
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 15, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -left-8 bottom-32 z-20 bg-white p-3 rounded-2xl shadow-xl border border-slate-100 hidden sm:block" data-id="element-122">
              <div className="flex items-center gap-3" data-id="element-123">
                <div className="bg-brand-100 p-2 rounded-full" data-id="element-124">
                  <Check className="w-5 h-5 text-brand-600" data-id="element-125" />
                </div>
                <div data-id="element-126">
                  <div className="text-xs text-slate-500 font-medium" data-id="element-127">Safe</div>
                  <div className="text-sm font-bold text-slate-900" data-id="element-128">
                    Natural Flavors
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Phone Frame */}
            <div className="relative z-10 bg-slate-900 rounded-[3rem] p-3 shadow-2xl ring-1 ring-slate-900/5" data-id="element-129">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-32 bg-slate-900 rounded-b-2xl z-20" data-id="element-130"></div>
              <div className="bg-white rounded-[2.5rem] overflow-hidden h-[640px] w-full relative" data-id="element-131">
                {/* App UI Mockup */}
                <div className="h-full flex flex-col" data-id="element-132">
                  {/* App Header */}
                  <div className="bg-brand-50 p-6 pt-12 pb-4" data-id="element-133">
                    <div className="flex justify-between items-center mb-4" data-id="element-134">
                      <ScanLine className="w-6 h-6 text-brand-600" data-id="element-135" />
                      <div className="w-8 h-8 bg-brand-200 rounded-full" data-id="element-136"></div>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900" data-id="element-137">
                      Scan Results
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-138">
                      Organic Granola Bar
                    </p>
                  </div>

                  {/* Scan Content */}
                  <div className="flex-1 p-6 bg-white overflow-hidden relative" data-id="element-139">
                    <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-brand-50 to-transparent z-10" data-id="element-140"></div>

                    <div className="space-y-4" data-id="element-141">
                      {/* Score Card */}
                      <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm mb-6" data-id="element-142">
                        <div className="flex items-center justify-between mb-2" data-id="element-143">
                          <span className="text-sm font-medium text-slate-500" data-id="element-144">
                            Health Score
                          </span>
                          <span className="px-2 py-1 bg-accent-100 text-accent-700 text-xs font-bold rounded-full" data-id="element-145">
                            Moderate
                          </span>
                        </div>
                        <div className="flex items-end gap-2" data-id="element-146">
                          <span className="text-4xl font-black text-slate-900" data-id="element-147">
                            65
                          </span>
                          <span className="text-sm text-slate-400 mb-1" data-id="element-148">
                            / 100
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 h-2 rounded-full mt-3 overflow-hidden" data-id="element-149">
                          <div className="w-[65%] h-full bg-accent-400 rounded-full" data-id="element-150"></div>
                        </div>
                      </div>

                      <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-151">
                        Ingredients Analysis
                      </h4>

                      {/* Ingredient List Items */}
                      <div className="space-y-3" data-id="element-152">
                        <div className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-100" data-id="element-153">
                          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-154" />
                          <div data-id="element-155">
                            <div className="font-bold text-slate-900 text-sm" data-id="element-156">
                              High Fructose Corn Syrup
                            </div>
                            <div className="text-xs text-red-600 mt-1" data-id="element-157">
                              Avoid: Linked to inflammation
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 bg-accent-50 rounded-xl border border-accent-100" data-id="element-158">
                          <div className="w-5 h-5 rounded-full bg-accent-200 flex items-center justify-center shrink-0 mt-0.5" data-id="element-159">
                            <span className="text-accent-700 text-xs font-bold" data-id="element-160">
                              !
                            </span>
                          </div>
                          <div data-id="element-161">
                            <div className="font-bold text-slate-900 text-sm" data-id="element-162">
                              Palm Oil
                            </div>
                            <div className="text-xs text-accent-600 mt-1" data-id="element-163">
                              Limit: High saturated fat
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 bg-brand-50 rounded-xl border border-brand-100" data-id="element-164">
                          <Check className="w-5 h-5 text-brand-500 shrink-0 mt-0.5" data-id="element-165" />
                          <div data-id="element-166">
                            <div className="font-bold text-slate-900 text-sm" data-id="element-167">
                              Whole Grain Oats
                            </div>
                            <div className="text-xs text-brand-600 mt-1" data-id="element-168">
                              Safe: Good source of fiber
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Action */}
                  <div className="p-6 border-t border-slate-100 bg-white" data-id="element-169">
                    <button className="w-full py-3 bg-brand-600 text-white font-bold rounded-xl shadow-lg shadow-brand-200" data-id="element-170">
                      Scan Next Item
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}